import { useState, useEffect } from "react";
import { Modal, Form, Button, Alert } from "react-bootstrap";
import { DEFAULT_SETTINGS, RemoteCareDemoSettings, SETTINGS_STORAGE_KEY } from "../util/utils";

interface RemoteCareDemoSettingsModalProps {
    show: boolean;
    onHide: () => void;
}

const RemoteCareDemoSettingsModal: React.FC<RemoteCareDemoSettingsModalProps> = ({
    show,
    onHide,
}) => {
    const [settings, setSettings] = useState<RemoteCareDemoSettings>(DEFAULT_SETTINGS);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load settings from local storage when modal opens
    useEffect(() => {
        if (show) {
            try {
                const storedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
                if (storedSettings) {
                    setSettings(JSON.parse(storedSettings));
                }
            } catch (err) {
                console.error("Error loading settings from local storage:", err);
                setError("Failed to load saved settings. Using defaults.");
            }
        }
    }, [show]);

    const handleSave = () => {
        try {
            localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
            setSaved(true);
            setError(null);
            setTimeout(() => {
                setSaved(false);
                onHide(); // Auto-close after successful save
            }, 1500);
        } catch (err) {
            console.error("Error saving settings to local storage:", err);
            setError("Failed to save settings. Please try again.");
        }
    };

    const handleReset = () => {
        setSettings(DEFAULT_SETTINGS);
        setSaved(false);
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Remote Care Demo Settings</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {saved && <Alert variant="success">Settings saved successfully!</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}

                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>API Prefix</Form.Label>
                        <Form.Control
                            type="text"
                            value={settings.apiPrefix}
                            onChange={(e) =>
                                setSettings({ ...settings, apiPrefix: e.target.value })
                            }
                            placeholder="Enter API URL prefix"
                        />
                        <Form.Text className="text-muted">
                            Base URL for API calls (e.g.,
                            https://localhost:8443/lobbie/api/open/v1/remote-care)
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>API Key</Form.Label>
                        <Form.Control
                            type="text"
                            value={settings.apiKey}
                            onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
                            placeholder="Enter API key"
                        />
                        <Form.Text className="text-muted">
                            Authentication key for API requests
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleReset}>
                    Reset to Defaults
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RemoteCareDemoSettingsModal;
